// Cookie-consent + marketing-tag management for the PUBLIC marketing site.
//
// NOTE ON STORAGE KEY: cookie consent is a per-device/per-browser decision made
// by anonymous visitors (no Firebase user exists yet), so — unlike the rest of
// the app — it is intentionally stored under a fixed device-level key rather
// than keyed by auth uid. Keying by uid is impossible pre-login and would also
// be the wrong GDPR model (consent belongs to the browsing device).
//
// Tags only ever fire on the marketing pages (never the authenticated teacher
// or pupil app), which keeps children's usage out of analytics/ad profiling.

import { logEvent } from 'firebase/analytics';
import { initAnalytics, setFirebaseConsent, getAnalyticsInstance } from '../firebase';

const CONSENT_KEY = 'classlabs_cookie_consent_v1';
const CONSENT_VERSION = 1;
// ICO guidance: re-ask for consent roughly every 12 months.
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

// Meta Pixel is managed in GTM (container GTM-KWZQB4NC), gated on the
// marketing_consent_granted dataLayer event. In-code loader is intentionally
// disabled (blank ID) so the pixel fires ONLY from GTM — no double-firing.
// (B2B Pixel ID for reference: 1449863363638209.)
export const META_PIXEL_ID = '';

// Pages where marketing/analytics tags are allowed to run.
const MARKETING_PATHS = ['/', '/sensiblesencoreview', '/register-interest', '/privacy-policy'];

export const isMarketingPath = (pathname = window.location.pathname) =>
  MARKETING_PATHS.includes(pathname);

// ---- Stored decision -------------------------------------------------------
export const getStoredConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    if (!parsed.ts || Date.now() - parsed.ts > MAX_AGE_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

export const saveConsent = (consent) => {
  const payload = {
    version: CONSENT_VERSION,
    ts: Date.now(),
    analytics: !!consent.analytics,
    marketing: !!consent.marketing,
  };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(payload)); } catch (_) { /* storage blocked */ }
  return payload;
};

export const clearConsent = () => {
  try { localStorage.removeItem(CONSENT_KEY); } catch (_) { /* ignore */ }
};

// Reopen the cookie banner so a visitor can change or withdraw their choice
// (GDPR: withdrawing consent must be as easy as giving it). The CookieConsent
// component listens for this event.
export const openCookieSettings = () => {
  window.dispatchEvent(new Event('cl:open-cookie-settings'));
};

// ---- Google Consent Mode v2 -----------------------------------------------
// Set a "denied" default early so any Google tag added later (GA4, Google Ads)
// respects consent from the first paint.
export const initConsentModeDefaults = () => {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  });
};

const updateGoogleConsentMode = (analyticsGranted, marketingGranted) => {
  if (!window.gtag) return;
  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: marketingGranted ? 'granted' : 'denied',
    ad_user_data: marketingGranted ? 'granted' : 'denied',
    ad_personalization: marketingGranted ? 'granted' : 'denied',
  });
};

// ---- Meta Pixel ------------------------------------------------------------
let metaPixelLoaded = false;
const loadMetaPixel = () => {
  if (metaPixelLoaded) return;
  if (!META_PIXEL_ID || META_PIXEL_ID === 'YOUR_META_PIXEL_ID') return; // not configured yet
  metaPixelLoaded = true;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
  document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
};

// Fire a Meta custom event. No-op until consent has loaded the pixel.
export const trackMarketingEvent = (name, params = {}) => {
  if (window.fbq) window.fbq('track', name, params);
};

// Push a marketing conversion event to the dataLayer for GTM to act on, but
// ONLY if the visitor granted marketing consent (keeps conversion tags gated).
// The matching GTM tag fires a Meta standard event (e.g. Lead) on this event.
export const trackConversion = (eventName, params = {}) => {
  const consent = getStoredConsent();
  if (!consent || !consent.marketing) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...params });
};

// Log a GA4 event via Firebase Analytics, but ONLY if the visitor granted
// ANALYTICS consent (GA4 is analytics, not marketing). No-op if analytics
// hasn't been initialised. Mark the event as a Key event in the GA4 dashboard.
export const trackGa4Event = (eventName, params = {}) => {
  const consent = getStoredConsent();
  if (!consent || !consent.analytics) return;
  const analytics = getAnalyticsInstance();
  if (analytics) logEvent(analytics, eventName, params);
};

// ---- Consent telemetry (cookieless, no identifiers) -----------------------
// Records aggregate accept/reject/impression counts so we can measure the
// accept rate. Sends NO cookies, NO auth, and NO user identifier — just the
// action, the marketing path, and a timestamp. Fire-and-forget.
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

export const logConsentMetric = (action) => {
  try {
    fetch(`${API_BASE}/consent-metric`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, path: window.location.pathname, ts: Date.now() }),
      credentials: 'omit',
      keepalive: true,
    }).catch(() => { /* best-effort telemetry */ });
  } catch (_) { /* ignore */ }
};

// ---- Apply a consent decision ---------------------------------------------
export const applyConsent = async (consent, pathname = window.location.pathname) => {
  // Never fire analytics/marketing tags outside the public marketing site.
  if (!isMarketingPath(pathname)) return;

  updateGoogleConsentMode(!!consent.analytics, !!consent.marketing);
  await setFirebaseConsent(!!consent.analytics);

  // Bridge consent to the dataLayer so GTM tags (e.g. the Meta Pixel, once moved
  // into GTM) can trigger on the visitor's marketing choice. Google Consent Mode
  // does NOT gate non-Google tags like the Meta Pixel, so GTM must trigger on this.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: consent.marketing ? 'marketing_consent_granted' : 'marketing_consent_denied',
  });

  if (consent.analytics) {
    await initAnalytics();
  }
  if (consent.marketing) {
    loadMetaPixel();
  }
};
